// 📄 incidenteService.ts
import {
  doc,
  collection,
  collectionGroup,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where,
  arrayUnion,
  serverTimestamp,
  deleteField,
  Timestamp
} from "firebase/firestore";
import { db } from '../firebase';
import { uploadAttachments, UploadedAttachment } from './storageService.ts';
import { queueEmail } from './mailService.ts';
import { notifySlackDeProyecto } from './slackService.ts';
import { ProjectMember } from './projectService.ts';

export type IncidentePrioridad = 'Baja' | 'Media' | 'Alta' | 'Crítica';
export type IncidenteEstado = 'Abierto' | 'En progreso' | 'Resuelto' | 'Cerrado';
export type IncidenteAmbiente = 'DEV' | 'QAS' | 'PRD' | 'Todos';
export type IncidenteFase = 'Proyecto' | 'Soporte';
export type IncidentePlataforma = 'Ariba' | 'CIG' | 'ERP' | 'Cloud Connector' | 'Otro';
export type IncidenteTipoAjuste = 'ABAP' | 'Configuración' | 'Mapeo' | 'Otro';
// "SR" (Service Request): mismo Incidente, con 3 campos extra que se
// piden al crearlo desde la pestaña SR de Integración (ver SRSection.tsx)
// — sistema, canal y categoría. Quedan opcionales para no romper
// incidentes creados antes de que existieran, o creados desde la sección
// general de Incidentes (que no los pide).
export type IncidenteCategoriaSR = 'Issue' | 'Habilitación' | 'Configuración';
export type IncidenteCanal =
  | 'generales' | 'oc' | 'expedicion' | 'confirmacion' | 'recepcion'
  | 'hes-ariba' | 'hes-erp' | 'facturas' | 'cutover';
export type IncidenteMotivoCierre =
  | 'Resuelto y confirmado'
  | 'Incidente temporal'
  | 'Cliente lo resolvió externamente'
  | 'Duplicado'
  | 'No reproducible'
  | 'Otro';

export const PRIORIDADES: IncidentePrioridad[] = ['Baja', 'Media', 'Alta', 'Crítica'];
export const ESTADOS: IncidenteEstado[] = ['Abierto', 'En progreso', 'Resuelto', 'Cerrado'];
export const AMBIENTES: IncidenteAmbiente[] = ['DEV', 'QAS', 'PRD', 'Todos'];
export const FASES: IncidenteFase[] = ['Proyecto', 'Soporte'];
export const FASE_LABELS: Record<IncidenteFase, string> = {
  Proyecto: 'Proyecto (Integración)',
  Soporte: 'Soporte'
};
export const PLATAFORMAS: IncidentePlataforma[] = ['Ariba', 'CIG', 'ERP', 'Cloud Connector', 'Otro'];
export const TIPOS_AJUSTE: IncidenteTipoAjuste[] = ['ABAP', 'Configuración', 'Mapeo', 'Otro'];
export const CATEGORIAS_SR: IncidenteCategoriaSR[] = ['Issue', 'Habilitación', 'Configuración'];
export const CANALES_INTEGRACION: { value: IncidenteCanal; label: string }[] = [
  { value: 'generales', label: 'Configuraciones Generales' },
  { value: 'oc', label: 'Orden de Compra' },
  { value: 'expedicion', label: 'Aviso de Expedición' },
  { value: 'confirmacion', label: 'Confirmación' },
  { value: 'recepcion', label: 'Recepción de Mercancía' },
  { value: 'hes-ariba', label: 'HES (desde Ariba)' },
  { value: 'hes-erp', label: 'HES (desde ERP)' },
  { value: 'facturas', label: 'Facturas' },
  { value: 'cutover', label: 'Cutover' }
];
export const MOTIVOS_CIERRE: IncidenteMotivoCierre[] = [
  'Resuelto y confirmado',
  'Incidente temporal',
  'Cliente lo resolvió externamente',
  'Duplicado',
  'No reproducible',
  'Otro'
];

export interface BitacoraEntry {
  autor: string;
  fecha: any;
  texto: string;
}

export interface IncidenteFormData {
  titulo: string;
  descripcion: string;
  reportadoPorNombre: string;
  reportadoPorEmail: string;
  fase: IncidenteFase;
  solucionRelacionada: string;
  // Solo aplica cuando fase === 'Proyecto' y solucionRelacionada === 'commerce':
  // clave construida con buildChecklistStepKey (ver data/commerceChecklistCatalog.ts).
  checklistStepKey?: string;
  ambiente: IncidenteAmbiente;
  prioridad: IncidentePrioridad;
  asignadoA: string; // '' = sin asignar
  // Campos de SR (ver comentario arriba de IncidenteCategoriaSR) — el
  // "sistema" es independiente de detalleResolucion.plataforma: este se
  // declara al crear el SR, aquel se completa al resolverlo (pueden
  // terminar siendo distintos, p. ej. se reportó como "ERP" pero al
  // investigar resultó ser un tema de "CIG").
  sistema?: IncidentePlataforma;
  canal?: IncidenteCanal | '';
  categoriaSR?: IncidenteCategoriaSR;
  // Referencia de vuelta al caso de prueba que originó este incidente (ver
  // botón "Crear incidente" en CasoPruebaCard.tsx, para un caso marcado
  // Fallido) — opcional, la mayoría de incidentes no vienen de un caso de
  // prueba. Solo se completa al crear, nunca se edita después.
  casoPruebaId?: string;
  // Ticket de la Mesa de ayuda que se escaló a este incidente (T4 de
  // plan-tickets-proyecto.md). Solo se completa al crear.
  ticketOrigenId?: string;
  ticketOrigenNumero?: string;
}

export interface DetalleResolucion {
  plataforma: IncidentePlataforma;
  queSeHizo: string;
  requirioAjusteTecnico: boolean;
  tipoAjuste?: IncidenteTipoAjuste;
  programaOTransaccion?: string;
}

export interface DetalleCierre {
  motivo: IncidenteMotivoCierre;
  comentario?: string;
}

// Referencia liviana a un registro de known_errors — se guarda una copia
// de code/title (no solo el id) para poder mostrar la card vinculada en
// el incidente sin tener que resolver el catálogo completo cada vez.
export interface KnownErrorRef {
  id: string;
  code: string;
  title: string;
}

export interface Incidente extends IncidenteFormData {
  id: string;
  estado: IncidenteEstado;
  fechaReporte?: any;
  adjuntos: UploadedAttachment[];
  bitacora: BitacoraEntry[];
  detalleResolucion?: DetalleResolucion;
  detalleCierre?: DetalleCierre;
  // Campos de "Resolución" (ver IncidenteDetailModal) — se completan desde
  // el modal de detalle, nunca al crear el incidente (recién reportado no
  // se sabe todavía por qué pasó ni si ya está documentado en el catálogo).
  causaRaiz?: string;
  solucionAplicada?: string;
  errorConocidoRef?: KnownErrorRef | null;
  // Solo aplica mientras errorConocidoRef es null — si se vincula un error
  // del catálogo, este campo se oculta en la UI pero NO se borra (por si
  // se desvincula más adelante).
  notaSapManual?: string;
  // Se setea server-side la primera vez que `estado` pasa a 'Resuelto'
  // (ver updateIncidenteEstado) — no editable manualmente.
  fechaResolucion?: any;
}

const escapeHtml = (str: string): string =>
  str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);

const incidentesRef = (projectId: string) => collection(db, "projects", projectId, "incidentes");
const incidenteRef = (projectId: string, incidenteId: string) => doc(db, "projects", projectId, "incidentes", incidenteId);

export const getIncidentes = async (projectId: string): Promise<Incidente[]> => {
  const snap = await getDocs(incidentesRef(projectId));
  return snap.docs
    .map(d => ({ id: d.id, ...(d.data() as Omit<Incidente, 'id'>) }))
    .sort((a, b) => (b.fechaReporte?.toMillis?.() ?? 0) - (a.fechaReporte?.toMillis?.() ?? 0));
};

// Incidente con el projectId inferido de su propio path (ver
// buscarIncidentesPorEmailAdmin) — solo lo usa la sección Soporte.
export interface IncidenteConProyecto extends Incidente {
  projectId: string;
}

/**
 * TODOS los incidentes reportados por un email, cruzando CUALQUIER
 * proyecto de la plataforma — para la sección Soporte (ver
 * SoporteHome.tsx): dado el email de quien reportó un problema, ver su
 * historial completo sin saber de antemano en qué proyecto(s) escribió.
 * Admin-only — ver firestore.rules, match /{path=**}/incidentes/{id} (un
 * match anidado normal NO le aplica a una collectionGroup, mismo hallazgo
 * ya confirmado con el emulador en tareas/invitacionesPendientes).
 */
export const buscarIncidentesPorEmailAdmin = async (email: string): Promise<IncidenteConProyecto[]> => {
  // Sin normalizar a minúscula a propósito: `reportadoPorEmail` se guarda
  // tal cual lo escribió quien reportó (createIncidente solo hace
  // `.trim()`, no `.toLowerCase()`) — el caller (SoporteHome.tsx) pasa el
  // email EXACTO ya resuelto desde users/{uid}.email, no texto libre
  // tipeado por el admin, así evita el desajuste de mayúsculas/minúsculas.
  const valor = email.trim();
  if (!valor) return [];
  const q = query(collectionGroup(db, "incidentes"), where("reportadoPorEmail", "==", valor));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, projectId: d.ref.parent.parent!.id, ...(d.data() as Omit<Incidente, "id">) }))
    .sort((a, b) => (b.fechaReporte?.toMillis?.() ?? 0) - (a.fechaReporte?.toMillis?.() ?? 0));
};

/**
 * Notifica por correo al/los PM del proyecto y a quien haya quedado
 * asignado al crear el incidente (ver mailService.ts — solo funciona una
 * vez instalada la extensión "Trigger Email"). No corta la creación del
 * incidente si el envío falla, solo lo deja loggeado.
 */
const notifyNuevoIncidente = async (data: IncidenteFormData, miembros: ProjectMember[]): Promise<void> => {
  const emails = new Set<string>();
  miembros.filter(m => m.rol === 'PM').forEach(m => emails.add(m.email));
  if (data.asignadoA) {
    const asignado = miembros.find(m => m.userId === data.asignadoA);
    if (asignado) emails.add(asignado.email);
  }
  if (emails.size === 0) return;

  await queueEmail({
    to: [...emails],
    subject: `[Incidente] ${data.titulo}`,
    html: `
      <p>Se reportó un nuevo incidente en el proyecto.</p>
      <p><strong>Título:</strong> ${escapeHtml(data.titulo)}</p>
      <p><strong>Prioridad:</strong> ${data.prioridad} &mdash; <strong>Ambiente:</strong> ${data.ambiente}</p>
      <p><strong>Solución relacionada:</strong> ${escapeHtml(data.solucionRelacionada)}</p>
      <p><strong>Reportado por:</strong> ${escapeHtml(data.reportadoPorNombre)} (${escapeHtml(data.reportadoPorEmail)})</p>
      <p><strong>Descripción:</strong></p>
      <p style="white-space:pre-wrap">${escapeHtml(data.descripcion)}</p>
    `
  });
};

/**
 * Crea el incidente, sube los adjuntos a Storage bajo su propio ID (mismo
 * patrón que createKnownError) y dispara la notificación por correo.
 */
export const createIncidente = async (
  projectId: string,
  data: IncidenteFormData,
  files: File[],
  miembros: ProjectMember[]
): Promise<string> => {
  if (!data.titulo.trim()) throw new Error("El título es obligatorio.");
  if (!data.descripcion.trim()) throw new Error("La descripción es obligatoria.");

  const ref = doc(incidentesRef(projectId));
  const adjuntos = files.length > 0
    ? await uploadAttachments(`projects/${projectId}/incidentes/${ref.id}`, files)
    : [];

  const payload: Record<string, any> = {
    titulo: data.titulo.trim(),
    descripcion: data.descripcion.trim(),
    reportadoPorNombre: data.reportadoPorNombre.trim(),
    reportadoPorEmail: data.reportadoPorEmail.trim(),
    fase: data.fase,
    solucionRelacionada: data.solucionRelacionada,
    ambiente: data.ambiente,
    prioridad: data.prioridad,
    asignadoA: data.asignadoA || '',
    estado: 'Abierto' as IncidenteEstado,
    adjuntos,
    bitacora: [],
    fechaReporte: serverTimestamp()
  };
  if (data.checklistStepKey) payload.checklistStepKey = data.checklistStepKey;
  if (data.sistema) payload.sistema = data.sistema;
  if (data.canal) payload.canal = data.canal;
  if (data.categoriaSR) payload.categoriaSR = data.categoriaSR;
  if (data.casoPruebaId) payload.casoPruebaId = data.casoPruebaId;
  if (data.ticketOrigenId) {
    payload.ticketOrigenId = data.ticketOrigenId;
    payload.ticketOrigenNumero = data.ticketOrigenNumero || '';
  }

  await setDoc(ref, payload);

  await notifyNuevoIncidente(data, miembros).catch(err => console.error('Error notificando incidente:', err));
  // "Notificaciones más ricas" (2026-08-20, Fase 3) — mismo evento, canal extra.
  notifySlackDeProyecto(projectId, `🚨 Nuevo incidente *${data.prioridad}*: ${data.titulo} (reportado por ${data.reportadoPorNombre})`)
    .catch(err => console.error('Error notificando incidente a Slack:', err));

  return ref.id;
};

/**
 * `fechaResolucionActual` es opcional pero necesario para el auto-set de
 * `fechaResolucion`: sin saber si ya tenía una fecha guardada no hay forma
 * de distinguir "pasa a Resuelto por primera vez" de "ya había estado
 * Resuelto antes" (p. ej. se reactivó y se vuelve a resolver) — en ese
 * segundo caso se conserva la fecha original en vez de pisarla.
 */
export const updateIncidenteEstado = async (
  projectId: string,
  incidenteId: string,
  estado: IncidenteEstado,
  fechaResolucionActual?: any
) => {
  const payload: Record<string, any> = { estado };
  if (estado === 'Resuelto' && !fechaResolucionActual) {
    payload.fechaResolucion = serverTimestamp();
  }
  await updateDoc(incidenteRef(projectId, incidenteId), payload);
};

export interface IncidenteDatosEditables {
  titulo: string;
  descripcion: string;
  fase: IncidenteFase;
  solucionRelacionada: string;
  checklistStepKey?: string;
  ambiente: IncidenteAmbiente;
  asignadoA: string;
  sistema?: IncidentePlataforma | '';
  canal?: IncidenteCanal | '';
  categoriaSR?: IncidenteCategoriaSR | '';
}

/**
 * Edita los datos "de contenido" del incidente (para corregir errores al
 * reportarlo) — separado de estado/prioridad/detalles, que ya tienen sus
 * propias funciones de guardado independiente. Deliberadamente no incluye
 * reportadoPorNombre/reportadoPorEmail (se tratan como dato histórico de
 * quién lo reportó, igual que fechaReporte) ni adjuntos.
 *
 * Bloqueado desde la UI cuando el incidente está "Resuelto"/"Cerrado" (ver
 * IncidenteDetailModal) — acá no se repite esa validación porque el botón
 * de edición ni siquiera se muestra en ese estado.
 */
export const updateIncidenteDatos = async (
  projectId: string,
  incidenteId: string,
  datos: IncidenteDatosEditables
) => {
  if (!datos.titulo.trim()) throw new Error("El título es obligatorio.");
  if (!datos.descripcion.trim()) throw new Error("La descripción es obligatoria.");

  const payload: Record<string, any> = {
    titulo: datos.titulo.trim(),
    descripcion: datos.descripcion.trim(),
    fase: datos.fase,
    solucionRelacionada: datos.solucionRelacionada,
    ambiente: datos.ambiente,
    asignadoA: datos.asignadoA || ''
  };
  // Si el cambio de fase/solución dejó de aplicar el checklist de Commerce,
  // hay que borrar el campo explícitamente — omitirlo del payload dejaría
  // colgado el valor viejo, ya no coherente con la nueva fase/solución.
  payload.checklistStepKey = datos.checklistStepKey ? datos.checklistStepKey : deleteField();
  payload.sistema = datos.sistema ? datos.sistema : deleteField();
  payload.canal = datos.canal ? datos.canal : deleteField();
  payload.categoriaSR = datos.categoriaSR ? datos.categoriaSR : deleteField();

  await updateDoc(incidenteRef(projectId, incidenteId), payload);
};

/**
 * Guardado independiente del cambio de estado (ver handleEstadoChange en
 * IncidenteDetailModal): el detalle es opcional, así que nunca bloquea
 * marcar "Resuelto"/"Cerrado", y se puede completar después reabriendo
 * el incidente. `tipoAjuste`/`programaOTransaccion` se omiten del payload
 * si no aplicó ajuste técnico, para no mandarle `undefined` a Firestore.
 */
export const updateDetalleResolucion = async (projectId: string, incidenteId: string, detalle: DetalleResolucion) => {
  const payload: DetalleResolucion = {
    plataforma: detalle.plataforma,
    queSeHizo: detalle.queSeHizo.trim(),
    requirioAjusteTecnico: detalle.requirioAjusteTecnico
  };
  if (detalle.requirioAjusteTecnico) {
    if (detalle.tipoAjuste) payload.tipoAjuste = detalle.tipoAjuste;
    if (detalle.programaOTransaccion?.trim()) payload.programaOTransaccion = detalle.programaOTransaccion.trim();
  }
  await updateDoc(incidenteRef(projectId, incidenteId), { detalleResolucion: payload });
};

export const updateDetalleCierre = async (projectId: string, incidenteId: string, detalle: DetalleCierre) => {
  const payload: DetalleCierre = { motivo: detalle.motivo };
  if (detalle.comentario?.trim()) payload.comentario = detalle.comentario.trim();
  await updateDoc(incidenteRef(projectId, incidenteId), { detalleCierre: payload });
};

export const updateIncidentePrioridad = async (projectId: string, incidenteId: string, prioridad: IncidentePrioridad) => {
  await updateDoc(incidenteRef(projectId, incidenteId), { prioridad });
};

/**
 * serverTimestamp() no se puede usar dentro de un elemento de array
 * (restricción de Firestore), por eso cada entrada de bitácora usa
 * Timestamp.now() en vez de serverTimestamp().
 */
export const addBitacoraEntry = async (projectId: string, incidenteId: string, autor: string, texto: string) => {
  if (!texto.trim()) return;
  const entry: BitacoraEntry = { autor, fecha: Timestamp.now(), texto: texto.trim() };
  await updateDoc(incidenteRef(projectId, incidenteId), {
    bitacora: arrayUnion(entry)
  });
};

export const updateCausaRaiz = async (projectId: string, incidenteId: string, causaRaiz: string) => {
  await updateDoc(incidenteRef(projectId, incidenteId), { causaRaiz: causaRaiz.trim() });
};

export const updateSolucionAplicada = async (projectId: string, incidenteId: string, solucionAplicada: string) => {
  await updateDoc(incidenteRef(projectId, incidenteId), { solucionAplicada: solucionAplicada.trim() });
};

export const updateNotaSapManual = async (projectId: string, incidenteId: string, notaSapManual: string) => {
  await updateDoc(incidenteRef(projectId, incidenteId), { notaSapManual: notaSapManual.trim() });
};

// No toca `notaSapManual` — se conserva aunque quede oculto en la UI
// mientras el vínculo esté activo (ver comentario en la interfaz Incidente).
export const linkErrorConocido = async (projectId: string, incidenteId: string, ref: KnownErrorRef) => {
  await updateDoc(incidenteRef(projectId, incidenteId), { errorConocidoRef: ref });
};

export const unlinkErrorConocido = async (projectId: string, incidenteId: string) => {
  await updateDoc(incidenteRef(projectId, incidenteId), { errorConocidoRef: null });
};

/**
 * Usado por la alerta de checklist en PRD: encuentra un incidente del mismo
 * paso de integración que sigue abierto en DEV o QAS, para avisar al
 * consultor antes de repetir el paso en PRD.
 */
export const buscarIncidenteAbiertoDelPaso = (
  incidentes: Incidente[],
  checklistStepKey: string
): Incidente | undefined =>
  incidentes.find(i =>
    i.fase === 'Proyecto' &&
    i.solucionRelacionada === 'commerce' &&
    i.checklistStepKey === checklistStepKey &&
    (i.ambiente === 'DEV' || i.ambiente === 'QAS') &&
    i.estado !== 'Resuelto' && i.estado !== 'Cerrado'
  );
